import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import SearchIcon from '@mui/icons-material/Search';
import { MoreVertOutlined as MoreVertOutlinedIcon } from '@mui/icons-material';
import TextSearch from '../../controls/TextSearch';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  OutlinedInput,
  IconButton,
  InputAdornment,
  TablePagination,
  Theme,
  Typography,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import { GridCellParams, GridColDef, DataGridPro } from '@mui/x-data-grid-pro';

import { Client } from '../../../data/models/client.model';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import * as clientSlice from '../../../data/client.slice';

import ClientWizard from './ClientWizard';
import { CellActions } from '../../tables/DataGridPro';
import ConfirmationDialog from '../../ConfirmationDialog';

const statusColors = (theme: Theme) => ({
  Onboarding: {
    backgroundColor: theme.palette.statusBackground.main,
    color: theme.palette.statusOnboarding.main,
  },
  Active: {
    backgroundColor: theme.palette.statusBackground.main,
    color: theme.palette.statusActive.main,
  },
});

const PREFIX = 'Clients';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  cardHeader: `${PREFIX}-cardHeader`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  grid: `${PREFIX}-grid`,
  statusCellContainer: `${PREFIX}-statusCellContainer`,
  statusCell: `${PREFIX}-statusCell`,
  statusText: `${PREFIX}-statusText`,
  statusDate: `${PREFIX}-statusDate`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: theme.palette.lightTextPrimary.main,
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    width: '327px',
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: theme.palette.lightActionDisabled60p.main,
    padding: `${theme.spacing(1.5)} 0`,

    '&::placeholder': {
      color: theme.palette.lightActionDisabled60p.main,
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: '#000',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '&.empty': {
      height: 300,
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        width: '100%',
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    [`& .MuiDataGrid-row`]: {
      letterSpacing: '0.17px',
      cursor: 'pointer',
      '& .MuiDataGrid-cell:last-child': {
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
    '& .MuiDataGrid-columnHeader[data-field="legalName"] .MuiDataGrid-iconButtonContainer':
      {
        width: 'unset',
        marginLeft: `${theme.spacing(-17.5)} !important`,
      },
    '& .MuiDataGrid-columnHeader[data-field="legalName"] .MuiDataGrid-sortIcon':
      {
        opacity: '1 !important',
        visibility: 'visible !important',
        transition: 'none !important',
      },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  [`& .${classes.statusCellContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  [`& .${classes.statusCell}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 64,
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    width: 'fit-content',

    [`& .${classes.statusText}, & .${classes.statusDate}`]: {
      fontFamily: 'Source Sans Pro',
      fontSize: 12,
      fontStyle: 'normal',
      lineHeight: '100%',
      letterSpacing: 0.17,
    },
    [`& .${classes.statusText}`]: {
      fontWeight: 600,
    },
    [`& .${classes.statusDate}`]: {
      fontWeight: 400,
    },
  },
}));

export default function Clients() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const clientsList = useAppSelector(clientSlice.selectClientsList);

  const loadingClientsList = useAppSelector(
    clientSlice.selectLoadingClientsList,
  );
  const loadedClientsList = useAppSelector(clientSlice.selectLoadedClientsList);
  const theme = useTheme();

  const savingClient = useAppSelector(clientSlice.selectSavingClient);
  const savedClient = useAppSelector(clientSlice.selectSavedClient);

  const isSuperReaderUser = useIsSuperReaderUser();

  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // More actions
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [moreActionsAnchorEl, setMoreActionsAnchorEl] =
    useState<null | HTMLElement>(null);
  const moreActionsOpen = Boolean(moreActionsAnchorEl);

  // Delete Client Dialog
  const [deleteClientDialogOpen, setDeleteClientDialogOpen] = useState(false);

  const [sortModel, setSortModel] = useState([
    { field: 'legalName', sort: 'asc' as 'asc' | 'desc' },
  ]);

  useEffect(() => {
    dispatch(clientSlice.fetchClientsListAsync(searchQuery, 1, 10000));
  }, [searchQuery]);

  const handleCreateClient = () => {
    dispatch(clientSlice.resetCurrentClientInfo());
    setClientDialogOpen(true);
  };

  const closeClientProfileDialog = () => {
    // if (clientData) {
    //   const client = clientUtils.getClientFromFormData(clientData);
    //   dispatch(clientSlice.createClientAsync(client));
    // }
    setClientDialogOpen(false);
  };

  const onSearchChange = (val: string) => {
    setSearchQuery(val);
    setPage(1);
  };

  const handleMoreActionsClick = (
    evt: React.MouseEvent<HTMLElement>,
    params: GridCellParams,
  ) => {
    evt.stopPropagation();
    console.log(params);
    setSelectedClient(params.row);
    setMoreActionsAnchorEl(evt.currentTarget);
  };

  const handleMoreActionsClose = () => {
    setMoreActionsAnchorEl(null);
  };

  const handleOpenDeleteClientDialog = () => {
    handleMoreActionsClose();
    setDeleteClientDialogOpen(true);
  };

  const handleCloseDeleteClientDialog = () => {
    setSelectedClient(null);
    setDeleteClientDialogOpen(false);
  };

  const handleDeleteClient = (client: Client | null) => {
    if (client) {
      dispatch(clientSlice.deleteClientAsync(client.clientBC));
    }
    handleCloseDeleteClientDialog();
    handleMoreActionsClose();
  };

  // Temporary sorting, searching functionality for the clients table until the backend is updated

  const clientsSortedByName = [...clientsList.items].sort((a, b) =>
    String(a.clientLegalName ?? '').localeCompare(
      String(b.clientLegalName ?? ''),
    ),
  );

  const sortedClients = useMemo(() => {
    let result = clientsSortedByName;
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];

      result = [...result].sort((a, b) => {
        const aValue = a[field as keyof typeof a];
        const bValue = b[field as keyof typeof b];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sort === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return sort === 'asc'
          ? String(aValue ?? '').localeCompare(String(bValue ?? ''))
          : String(bValue ?? '').localeCompare(String(aValue ?? ''));
      });
    }

    return result;
  }, [clientsList, sortModel]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedClients = sortedClients.slice(startIndex, endIndex);

  const renderDate = (params: GridCellParams): React.ReactElement => {
    let date = '';
    if (params.value) {
      date = DateTime.fromISO(params.value as string).toLocaleString(
        DateTime.DATE_SHORT,
      );
    }
    return <>{date}</>;
  };
  const columns: GridColDef[] = [
    {
      field: 'clientLegalName',
      headerName: t('client.name'),
      width: 195,
      sortable: true,
    },
    {
      field: 'clientAbbreviation',
      headerName: t('client.abbreviation'),
      width: 190,
      sortable: false,
    },
    {
      field: 'effectiveStartDate',
      headerName: t('client.start'),
      width: 190,
      sortable: false,
      renderCell: renderDate,
    },
    {
      field: 'effectiveEndDate',
      headerName: t('client.end'),
      width: 190,
      sortable: false,
      renderCell: renderDate,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      align: 'center',
      sortable: false,
      renderHeader: () => (
        <Typography align="center">{t('client.status')}</Typography>
      ),
      renderCell: (params) => (
        <Box className={classes.statusCellContainer}>
          <Box
            className={classes.statusCell}
            // sx={statusColors[params.row.status as keyof typeof statusColors]}
            sx={statusColors(theme)['Onboarding']}>
            <Typography className={classes.statusText}>
              Onboarding
              {/* {params.row.status} */}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      width: 100,
      sortable: false,
      filterable: false,
      flex: 1,
      // disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        const canDeleteClient =
          !isSuperReaderUser && params.row.clientState !== 'active';
        if (canDeleteClient) {
          return (
            <CellActions>
              <Tooltip title={t('common.moreActions')}>
                <IconButton
                  size="large"
                  onClick={(evt) => handleMoreActionsClick(evt, params)}>
                  <MoreVertOutlinedIcon />
                </IconButton>
              </Tooltip>
            </CellActions>
          );
        }
        return <></>;
      },
    },
  ];

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <CardHeader
          title={`${t('client.searchTitle')}`}
          className={classes.cardHeader}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <TextSearch
                id="text-search"
                icon={<SearchIcon />}
                label={t('client.search')}
                currentSearchFilter={searchQuery}
                onSearchChange={onSearchChange}
              />
            </Box>
            {!isSuperReaderUser && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateClient}>
                {t('client.add')}
              </Button>
            )}
          </Box>
          <DataGridPro
            loading={loadingClientsList || savingClient}
            rows={paginatedClients}
            getRowId={(row) => row.clientBC}
            columns={columns as GridColDef[]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(newSortModel) =>
              setSortModel(
                newSortModel as { field: string; sort: 'asc' | 'desc' }[],
              )
            }
            className={`${classes.grid} ${sortedClients.length === 0 ? 'empty' : ''}`}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            hideFooterRowCount
            hideFooter
            rowHeight={56}
            disableColumnMenu={true}
            disableColumnResize={true}
            onCellClick={(params, event) => {
              const id = encodeURIComponent(params.row.clientBC);
              navigate(`/clients/${id}`);
            }}
          />
          <TablePagination
            component="div"
            count={sortedClients.length}
            page={page - 1}
            onPageChange={(event, page) => setPage(page + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number.parseInt(event.target.value, 10));
              setPage(1);
            }}
          />
        </CardContent>
      </Card>
      <ClientWizard
        title={t('client.addClient')}
        open={clientDialogOpen}
        close={closeClientProfileDialog}
      />
      <Menu
        anchorEl={moreActionsAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={moreActionsOpen}
        onClose={handleMoreActionsClose}>
        <MenuItem onClick={(evt) => handleOpenDeleteClientDialog()}>
          {t('common.delete')}
        </MenuItem>
      </Menu>
      <ConfirmationDialog
        title={t('client.confirmDeleteClientTitle')}
        message={t('client.confirmDeleteClientMessage')}
        open={deleteClientDialogOpen}
        positiveAction={() => handleDeleteClient(selectedClient)}
        negativeAction={() => handleCloseDeleteClientDialog()}
      />
    </StyledBox>
  );
}
